import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Loader2, RefreshCw, Mail, Building2, User, Send, CheckCircle2, Clock, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'pending_agent_signup', label: 'Pending signup' },
  { value: 'agent_signed_up', label: 'Signed up' },
];

export default function AgentNominations() {
  const [loading, setLoading] = useState(true);
  const [nominations, setNominations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending_agent_signup');
  const [search, setSearch] = useState('');
  const [resendingId, setResendingId] = useState(null);

  const token = localStorage.getItem('session_token');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/agent-nominations?status=${encodeURIComponent(statusFilter)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to load nominations');
      setNominations(data.nominations || []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load nominations');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const handleResend = async (nom) => {
    setResendingId(nom.nomination_id);
    try {
      const res = await fetch(`${API_URL}/api/platform-admin/agent-nominations/${nom.nomination_id}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to resend invitation');
      toast.success(`Invitation resent to ${nom.agent_email}`);
      load();
    } catch (err) {
      toast.error(err?.message || 'Could not resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = nominations.filter((n) => {
    if (!q) return true;
    return (n.agent_email || '').toLowerCase().includes(q)
      || (n.agent_name || '').toLowerCase().includes(q)
      || (n.building_name || '').toLowerCase().includes(q)
      || (n.nominated_by_email || '').toLowerCase().includes(q);
  });

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Mail className="w-6 h-6 text-indigo-600" />
              Nominated Agents
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Managing agents nominated by residents/trustees during building creation. Residents
              are read-only until their agent signs up and approves them — nudge stale invites here.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="agent-nominations-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setStatusFilter(s.value)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border transition ${
                      statusFilter === s.value
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`nomination-tab-${s.value}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Search by agent name / email, building, resident…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="nomination-search-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-16 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="font-medium">Nothing to chase.</p>
                <p className="text-sm">No {statusFilter === 'pending_agent_signup' ? 'pending' : 'signed-up'} nominations{q ? ' match your search' : ''}.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filtered.map((n) => (
                  <div key={n.nomination_id} className="py-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-900">{n.agent_name || n.agent_email}</span>
                        <span className="text-xs text-slate-500">{n.agent_email}</span>
                        {n.agent_has_account ? (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 border-emerald-200">Signed up</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
                            <Clock className="w-3 h-3 mr-1" />Pending
                          </Badge>
                        )}
                        {n.resent_count > 0 && (
                          <Badge variant="outline" className="text-xs">
                            Nudged {n.resent_count}x
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                        {n.agent_company && (
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />{n.agent_company}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5 text-indigo-500" /><strong>{n.building_name || n.hoa_id}</strong>
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />Nominated by {n.nominated_by_email}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-400">
                        Created {formatDate(n.created_at)}
                        {n.last_resent_at && <> · Last reminded {formatDate(n.last_resent_at)}</>}
                      </div>
                    </div>
                    {statusFilter === 'pending_agent_signup' && !n.agent_has_account && (
                      <Button
                        size="sm"
                        onClick={() => handleResend(n)}
                        disabled={resendingId === n.nomination_id}
                        data-testid={`resend-nomination-${n.nomination_id}`}
                      >
                        {resendingId === n.nomination_id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Resend invitation
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
